import React, { useEffect, useRef } from 'react';

const WORDS = ['So', 'I', 'was', 'like'];
const PER_WORD = 420;
const SENTENCE_MS = WORDS.length * PER_WORD;
const HOLD_MS = 900;
const GAP_MS = 400;
const CYCLE_MS = SENTENCE_MS + HOLD_MS + GAP_MS;

interface KineticPresetPreviewProps {
  presetId: string;
}

const KineticPresetPreview: React.FC<KineticPresetPreviewProps> = ({ presetId }) => {
  const soloRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const sweepRef = useRef<HTMLSpanElement>(null);
  const typeRef = useRef<HTMLSpanElement>(null);
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let lastShown = -1;

    const render = (t: number) => {
      const speaking = t < SENTENCE_MS;
      const holding = !speaking && t < SENTENCE_MS + HOLD_MS;
      const visible = speaking || holding;
      const activeIdx = speaking ? Math.min(WORDS.length - 1, Math.floor(t / PER_WORD)) : WORDS.length - 1;

      switch (presetId) {
        case 'word-pop':
        case 'bounce-in': {
          if (soloRef.current) {
            const shown = visible ? WORDS[activeIdx] : '';
            if (shown !== soloRef.current.textContent || activeIdx !== lastShown) {
              soloRef.current.textContent = shown;
              soloRef.current.style.animation = 'none';
              void soloRef.current.offsetWidth;
              soloRef.current.style.animation = presetId === 'bounce-in'
                ? 'kcBounceIn 320ms cubic-bezier(.2,.9,.3,1.3)'
                : 'kcPopIn 320ms cubic-bezier(.2,.9,.3,1.3)';
              lastShown = activeIdx;
            }
          }
          break;
        }
        case 'karaoke-box':
        case 'karaoke-color':
        case 'karaoke-scale': {
          wordRefs.current.forEach((el, i) => {
            if (!el) return;
            el.style.opacity = visible ? '1' : '0';
            const isActive = visible && i === activeIdx;
            el.classList.toggle('kc-mini-box', presetId === 'karaoke-box' && isActive);
            el.classList.toggle('kc-mini-color', presetId === 'karaoke-color' && isActive);
            el.classList.toggle('kc-mini-scale', presetId === 'karaoke-scale' && isActive);
          });
          break;
        }
        case 'build-up': {
          wordRefs.current.forEach((el, i) => {
            if (!el) return;
            const shown = visible && i <= activeIdx;
            el.style.opacity = shown ? '1' : '0';
          });
          break;
        }
        case 'underline-marker': {
          wordRefs.current.forEach((el) => { if (el) el.style.opacity = visible ? '1' : '0'; });
          if (barRef.current) {
            const active = wordRefs.current[activeIdx];
            if (visible && active) {
              barRef.current.style.opacity = '1';
              barRef.current.style.left = active.offsetLeft + 'px';
              barRef.current.style.width = active.offsetWidth + 'px';
            } else {
              barRef.current.style.opacity = '0';
            }
          }
          break;
        }
        case 'fill-sweep': {
          if (sweepRef.current) {
            const pct = speaking ? (t / SENTENCE_MS) * 100 : (holding ? 100 : 0);
            sweepRef.current.style.setProperty('--kc-sweep', pct + '%');
            sweepRef.current.style.opacity = visible ? '1' : '0';
          }
          break;
        }
        case 'typewriter': {
          if (typeRef.current) {
            const full = WORDS.join(' ');
            const chars = holding ? full.length : Math.floor((Math.min(t, SENTENCE_MS) / SENTENCE_MS) * full.length);
            typeRef.current.textContent = visible ? full.slice(0, Math.max(0, chars)) : '';
          }
          break;
        }
        default:
          break;
      }
    };

    if (reduced) {
      render(SENTENCE_MS * 0.6);
    } else {
      const loop = (now: number) => {
        render(now % CYCLE_MS);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => cancelAnimationFrame(raf);
  }, [presetId]);

  if (presetId === 'word-pop' || presetId === 'bounce-in') {
    return <span ref={soloRef} className="kc-mini-solo" />;
  }

  if (presetId === 'fill-sweep') {
    return <span ref={sweepRef} className="kc-mini-sweep">{WORDS.join(' ')}</span>;
  }

  if (presetId === 'typewriter') {
    return (
      <span className="kc-mini-type-wrap">
        <span ref={typeRef} className="kc-mini-type" />
        <span className="kc-mini-caret" />
      </span>
    );
  }

  if (presetId === 'underline-marker') {
    return (
      <div ref={trackRef} className="kc-mini-track">
        {WORDS.map((w, i) => (
          <span key={w + i} ref={el => { wordRefs.current[i] = el; }} className="kc-mini-word">{w}</span>
        ))}
        <span ref={barRef} className="kc-mini-bar" />
      </div>
    );
  }

  // karaoke-box, karaoke-color, build-up, karaoke-scale fallback
  return (
    <div ref={wrapRef} className="kc-mini-wrap">
      {WORDS.map((w, i) => (
        <span key={w + i} ref={el => { wordRefs.current[i] = el; }} className="kc-mini-word">{w}</span>
      ))}
    </div>
  );
};

export default KineticPresetPreview;
